import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getBranding, updateBranding } from "@/lib/api";
import { toast } from "sonner";
import { Save, ImageIcon } from "lucide-react";

export default function Branding() {
  const [b, setB] = useState({ brand_company: "", brand_color: "#E27D60", brand_logo_url: "", brand_tagline: "" });
  const [allowed, setAllowed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBranding();
        setAllowed(!!data.can_white_label);
        setB({
          brand_company: data.brand_company || "",
          brand_color: data.brand_color || "#E27D60",
          brand_logo_url: data.brand_logo_url || "",
          brand_tagline: data.brand_tagline || "",
        });
      } finally { setLoaded(true); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBranding(b);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-8">
          <div className="tag-label text-[#E27D60]">White-label</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Brand your reports</h1>
          <p className="text-[#1E392A]/60 mt-1">Your logo, color, and company name on every shared report — perfect for client deliverables.</p>
        </div>

        {!allowed && loaded && (
          <div className="rounded-3xl bg-white border border-dashed border-[#E27D60]/40 p-6 mb-8">
            <div className="font-display text-2xl">Agency plan required</div>
            <p className="text-[#1E392A]/65 mt-1">White-label is part of the $79/mo Agency plan.</p>
            <Link to="/billing" className="mt-4 inline-flex h-10 px-5 items-center rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white text-sm" data-testid="branding-upgrade-link">
              Upgrade to Agency
            </Link>
          </div>
        )}

        <div className="rounded-3xl bg-white border border-black/5 p-6 md:p-8 bento-shadow space-y-5">
          <div>
            <Label className="tag-label text-[#1E392A]/55">Company name</Label>
            <Input
              value={b.brand_company}
              disabled={!allowed}
              onChange={(e) => setB({ ...b, brand_company: e.target.value })}
              placeholder="e.g. Stout Marketing Co."
              className="mt-2 rounded-xl"
              data-testid="brand-company-input"
            />
          </div>
          <div>
            <Label className="tag-label text-[#1E392A]/55">Tagline (optional)</Label>
            <Input
              value={b.brand_tagline}
              disabled={!allowed}
              onChange={(e) => setB({ ...b, brand_tagline: e.target.value })}
              placeholder="e.g. Marketing analytics for serious operators"
              className="mt-2 rounded-xl"
              data-testid="brand-tagline-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <Label className="tag-label text-[#1E392A]/55">Logo URL</Label>
              <Input
                value={b.brand_logo_url}
                disabled={!allowed}
                onChange={(e) => setB({ ...b, brand_logo_url: e.target.value })}
                placeholder="https://yourdomain.com/logo.png"
                className="mt-2 rounded-xl"
                data-testid="brand-logo-input"
              />
              <p className="text-xs text-[#1E392A]/50 mt-1">Public image URL. Square format works best.</p>
            </div>
            <div>
              <Label className="tag-label text-[#1E392A]/55">Brand color</Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="color"
                  value={b.brand_color}
                  disabled={!allowed}
                  onChange={(e) => setB({ ...b, brand_color: e.target.value })}
                  className="w-14 h-10 p-1 rounded-xl"
                  data-testid="brand-color-input"
                />
                <Input
                  value={b.brand_color}
                  disabled={!allowed}
                  onChange={(e) => setB({ ...b, brand_color: e.target.value })}
                  className="flex-1 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <Label className="tag-label text-[#1E392A]/55">Preview</Label>
            <div className="mt-2 rounded-2xl bg-[#F7F5F0] p-4 flex items-center gap-3 border border-black/5">
              {b.brand_logo_url ? (
                <img src={b.brand_logo_url} alt="" className="w-12 h-12 rounded-xl object-cover bg-white" />
              ) : (
                <div className="w-12 h-12 rounded-xl grid place-items-center text-white font-display text-xl" style={{ background: b.brand_color }}>
                  <ImageIcon className="w-5 h-5" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-display text-xl truncate" style={{ color: b.brand_color }}>{b.brand_company || "Your company"}</div>
                <div className="text-sm text-[#1E392A]/60 truncate">{b.brand_tagline || "Your tagline here"}</div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={!allowed || saving} className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white" data-testid="save-branding-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save branding"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
